import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsPrescription2 } from "react-icons/bs";
import { Link } from "react-router-dom";
import { AreaChart } from "@mantine/charts";
import {
 Anchor,  Button,
  Center,
  Grid,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip } from "@mantine/core";
import { useClickOutside } from "@mantine/hooks";
import { IconPillFilled } from "@tabler/icons-react";

import { DateRangeSelect } from "@pages/AdminDashboard/components/DateRangeSelect";
import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION } from "@constants/index";

import { useGetPrescriptionActivity } from "@hooks/queries/dashboard/useGetPrescriptionActivity";
import { useGetPrescriptionsTrend } from "@hooks/queries/dashboard/useGetPrescriptionsTrend";
import { useGetTotalPrescriptions } from "@hooks/queries/dashboard/useGetTotalPrescriptions";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { useAuth } from "@contexts/auth";


export const Prescriptions = () => {
  const { t, i18n } = useTranslation();
  const { id: userId } = useAuth();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [prescriptionActivityDateRange, setPrescriptionActivityDateRange] =
    useState<string | null>("last-7-days");
  const [
    customPrescriptionActivityDateRange,
    setCustomPrescriptionActivityDateRange,
  ] = useState<[Date | null, Date | null]>([null, null]);
  const [isTooltipOpened, setIsTooltipOpened] = useState(false);
  const [currentRowId, setCurrentRowId] = useState("-1");
  const ref = useClickOutside(() => setIsTooltipOpened(false));
  const [trendTarget, setTrendTarget] = useState<number | string>();
  const [currentPage, setCurrentPage] = useState(1);
  const { columnCount, tableRef } = useTableColumnCount();

  const {
    data: prescriptionsTrend,
    isLoading: isPrescriptionsTrendLoading,
    isSuccess: isPrescriptionsTrendSuccess,
    isError: isPrescriptionsTrendError,
  } = useGetPrescriptionsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: prescriptions,
    isLoading: isPrescriptionsLoading,
    isSuccess: isPrescriptionsSuccess,
    isError: isPrescriptionsError,
  } = useGetTotalPrescriptions({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    userId: userId ?? "",
  });
  const {
    data: prescriptionActivityData,
    isLoading: isPrescriptionActivityLoading,
    isSuccess: isPrescriptionActivitySuccess,
    isError: isPrescriptionActivityError,
  } = useGetPrescriptionActivity({
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    dateRange: prescriptionActivityDateRange as string,
    ...(prescriptionActivityDateRange === "custom" &&
      getCustomDateRange(customPrescriptionActivityDateRange)),
    userId: userId ?? "",
  });

  const prescriptionRows = useMemo(() => {
    return prescriptionActivityData?.newData?.map(item => (
      <Table.Tr key={item.id}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(item.date as Date)}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.date?.toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Group gap={8} align="start">
            <Stack gap={5}>
              <Anchor
                component={Link}
                to={`/patients/${item.patientId}/consultation/general/${item.consultationId}`}
                fz={14}
                c="accent"
                ff={"var(--font-secondary)"}>
                {item.cId}
              </Anchor>
              <Text c="dimmed" fz={13}>
                {formatDateToString(item.consultationDate as Date)},{" "}
                {new Date(item.consultationDate as Date).toLocaleTimeString(
                  i18n.language,
                  { timeStyle: "short" }
                )}
              </Text>
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td c={item.medications.length ? "black" : "dimmed"}>
          {item.medications?.length ? (
            <Group gap="xs">
              <Text fz={14}>{item.medications?.at(0)}</Text>
              {item.medications.length > 1 && (
                <Tooltip
                  fz={14}
                  label={item.medications?.slice(1).join(", ")}
                  multiline
                  maw={500}
                  withArrow
                  opened={isTooltipOpened && currentRowId === item.id}>
                  <Button
                    size="compact-xs"
                    bg="gray.2"
                    c="black"
                    ref={ref}
                    onClick={() => {
                      setCurrentRowId(item.id);
                      setIsTooltipOpened(a => !a);
                    }}>
                    +{" "}
                    {t("plus-x-more", {
                      amount: item.medications.length - 1,
                    })}
                  </Button>
                </Tooltip>
              )}
            </Group>
          ) : (
            "N/A"
          )}
        </Table.Td>
        <Table.Td>{item.physician}</Table.Td>
      </Table.Tr>
    ));
  }, [prescriptionActivityData, i18n.language, isTooltipOpened]);

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={"staff"}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-prescriptions")}
            amount={prescriptions?.totalPrescriptions || 0}
            isLoading={isPrescriptionsLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <BsPrescription2
                  size={16}
                  //   stroke={"1"}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("total-medications")}
            amount={prescriptions?.totalMedications || 0}
            isLoading={isPrescriptionsLoading}
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconPillFilled
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("prescriptions-over-time")}
                </Title>

                <NumberInput
                  w={150}
                  min={0}
                  value={trendTarget}
                  onChange={setTrendTarget}
                  placeholder={t("type-target")}
                />
              </Group>

              {renderLoadingErrorData({
                isLoading: isPrescriptionsTrendLoading,
                isError: isPrescriptionsTrendError,
                isSuccess: isPrescriptionsTrendSuccess,
                dataLength: prescriptionsTrend?.length || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: <Skeleton h={300} w={"100%"} mt={"1rem"} />,
                dataComponent: (
                  <AreaChart
                    mt={"1rem"}
                    h={300}
                    data={prescriptionsTrend || []}
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    referenceLines={
                      trendTarget
                        ? [
                            {
                              y: trendTarget,
                              color: "red.8",
                              label: t("target"),
                              labelPosition: "insideBottomLeft",
                            },
                          ]
                        : []
                    }
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={[{ name: "prescriptions", color: "indigo.5" }]}
                    tickLine="y"
                    // yAxisProps={{ domain: [0, 700] }}
                    curveType="linear"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("prescription-activity")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button> */}
                  <DateRangeSelect
                    dateRange={prescriptionActivityDateRange}
                    setDateRange={newRange => {
                      setPrescriptionActivityDateRange(newRange);
                      setCurrentPage(1); // Reset to first page when date range changes
                    }}
                    customDateRange={customPrescriptionActivityDateRange}
                    setCustomDateRange={newRange => {
                      setCustomPrescriptionActivityDateRange(newRange);
                    }}
                  />
                </Group>
              </Group>

              <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={tableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"} h={67}>
                      <Table.Th>{t("date-created")}</Table.Th>
                      <Table.Th>{t("consultation-id")}</Table.Th>
                      <Table.Th>{t("medications")}</Table.Th>
                      <Table.Th>{t("doctor")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isPrescriptionActivityLoading,
                      isError: isPrescriptionActivityError,
                      isSuccess: isPrescriptionActivitySuccess,
                      dataLength:
                        prescriptionActivityData?.newData?.length || 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          message={t("empty-prescriptions")}
                          colSpan={columnCount}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={columnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={columnCount}
                          skeletonProps={{ widths: ["md", "md", "lg", "lg"] }}
                        />
                      ),
                      dataComponent: prescriptionRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={prescriptionActivityData?.pagination || {}}
                paginatedRows={prescriptionActivityData?.newData?.length || 0}
                onPageChange={setCurrentPage}
                isLoading={isPrescriptionActivityLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
